/**
 * GateWalletConnectButton Component
 *
 * Button for connecting to Gate Wallet.
 * Features:
 * - Gate Wallet branding (blue/green theme)
 * - Direct auto-connect to Gate Wallet (like Solflare)
 * - Auto-redirects to download page if Gate Wallet is not installed
 * - Auto-authentication after connection
 * - Hover tooltip showing campaign info
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppKit, useAppKitState } from '@reown/appkit/react';
import { ConnectorController, ConnectorControllerUtil } from '@reown/appkit-controllers';

import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { logger } from '@/lib/logger';
import config from '@/config';
import {
  isGateWalletInstalled,
  GATE_WALLET_DOWNLOAD_URL,
  getGateWalletAdapter,
} from '@/adapters/GateWalletAdapter';

// Gate Wallet brand colors
const GATE_GREEN = '#17E6A1';
const GATE_BLUE = '#2354E6';

export function GateWalletConnectButton() {
  const { open } = useAppKit();
  const { open: isModalOpen } = useAppKitState();
  const { isConnected, walletType } = useWalletAddress();
  const { authenticate, isAuthenticated } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const hasAuthenticatedRef = useRef(false);
  const shouldAuthenticateRef = useRef(false);

  // Handle authentication after connection
  useEffect(() => {
    if (!isConnected || !shouldAuthenticateRef.current || hasAuthenticatedRef.current) {
      if (!isConnected) {
        setIsConnecting(false);
      }
      return;
    }

    let cancelled = false;
    hasAuthenticatedRef.current = true;
    shouldAuthenticateRef.current = false;

    const authenticateWallet = async () => {
      try {
        logger.log('[GateWalletConnectButton] Running authentication after connection');
        const success = await authenticate();
        if (!cancelled) {
          if (success) {
            toast.success('Connected with Gate Wallet!');
          }
        }
      } catch (error) {
        if (!cancelled) {
          logger.error('[GateWalletConnectButton] Authentication failed:', error);
          toast.error(error instanceof Error ? error.message : 'Authentication failed');
          hasAuthenticatedRef.current = false;
        }
      } finally {
        if (!cancelled) {
          setIsConnecting(false);
        }
      }
    };

    authenticateWallet();

    return () => {
      cancelled = true;
    };
  }, [authenticate, isConnected]);

  // Reset flags when disconnected
  useEffect(() => {
    if (!isConnected) {
      hasAuthenticatedRef.current = false;
      shouldAuthenticateRef.current = false;
    }
  }, [isConnected]);

  // If the AppKit modal was opened but the user closed it without connecting,
  // ensure we clear the local "connecting" UI state.
  useEffect(() => {
    if (!isModalOpen && !isConnected) {
      setIsConnecting(false);
      shouldAuthenticateRef.current = false;
    }
  }, [isModalOpen, isConnected]);

  // Check feature flag - must be after hooks
  if (!config.ENABLE_GATE_WALLET_CAMPAIGN) {
    return null;
  }

  const handleConnect = async () => {
    // If already connected, just authenticate
    // Note: if we're connected via the legacy Gate injected adapter on devnet, we still
    // route through AppKit because Gate's injected Solana signer is effectively mainnet-only.
    if (isConnected && !isAuthenticated && walletType !== 'gate') {
      logger.log('[GateWalletConnectButton] Already connected, authenticating');
      setIsConnecting(true);
      try {
        const success = await authenticate();
        if (success) {
          toast.success('Connected with Gate Wallet!');
        }
      } catch (error) {
        logger.error('[GateWalletConnectButton] Authentication failed:', error);
        toast.error(error instanceof Error ? error.message : 'Authentication failed');
      } finally {
        setIsConnecting(false);
      }
      return;
    }

    // In development (devnet), always use AppKit connection for Gate Wallet.
    // Runtime evidence: injected Gate signer disables confirm for devnet txs.
    shouldAuthenticateRef.current = true;
    setIsConnecting(true);

    try {
      if (!isGateWalletInstalled()) {
        // Gate Wallet not installed - redirect to download page
        logger.log('[GateWalletConnectButton] Gate Wallet not installed, redirecting to download');
        window.open(GATE_WALLET_DOWNLOAD_URL, '_blank');
        setIsConnecting(false);
        shouldAuthenticateRef.current = false;
        return;
      }

      logger.log('[GateWalletConnectButton] Opening AppKit and auto-selecting Gate Wallet (WalletConnect)');

      // Ensure modal is open on Connect view (solana)
      await open({ view: 'Connect', namespace: 'solana' });

      // Gate Wallet does not appear in the WalletConnect registry for Solana (it only advertises EVM chains),
      // so we can't reliably auto-select it via ApiController.fetchWallets.
      //
      // Instead, auto-connect via the Solana Wallet Standard announced connector (if present).
      const solanaConnectors = ConnectorController.getConnectors('solana');
      const normalize = (s: any) => String(s || '').toLowerCase().replace(/\s+/g, '');
      const gateConnector =
        solanaConnectors.find((c: any) => normalize(c?.name) === 'gatewallet' || normalize(c?.name) === 'gatewallet') ??
        solanaConnectors.find((c: any) => normalize(c?.name).includes('gate'));

      if (gateConnector) {
        await ConnectorControllerUtil.connectExternal(gateConnector as any);
        // Authentication will be handled by the useEffect once connected
      } else {
        toast.message('Select your Gate Wallet (Browser Wallet) in the modal to continue');
      }
    }
    // If we throw here, the modal may still be open; rely on modal-close effect to clear isConnecting.
     
    catch (error: any) {
      logger.error('[GateWalletConnectButton] AppKit Gate connect failed:', error);
      toast.error(error?.message || 'Failed to connect with Gate Wallet');
      setIsConnecting(false);
      shouldAuthenticateRef.current = false;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleConnect}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={isConnecting}
        className={cn(
          'px-2 py-2 rounded-lg transition-all duration-200 border',
          'text-xs font-medium tracking-tight',
          'w-full md:w-[140px] h-9 flex items-center justify-center gap-1.5',
          'transform hover:scale-[1.02] active:scale-[0.98]',
          isConnecting
            ? 'opacity-80 cursor-not-allowed'
            : 'hover:shadow-lg cursor-pointer'
        )}
        style={{
          fontFamily: 'Tiny5, monospace',
          background: `linear-gradient(135deg, ${GATE_BLUE} 0%, ${GATE_GREEN} 100%)`,
          color: '#FFFFFF',
          borderColor: GATE_GREEN,
          boxShadow: `0 0 18px rgba(35, 84, 230, 0.4), 0 0 12px rgba(23, 230, 161, 0.3)`,
        }}
      >
        {isConnecting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
            <span>Connecting...</span>
          </>
        ) : (
          <>
            <div className="rounded px-0.5 py-0.5 flex-shrink-0" style={{ background: 'rgba(240, 240, 240, 0.95)' }}>
              <img src="/gate-color-short.png" alt="Gate" className="h-3.5" />
            </div>
            <span>Gate Wallet</span>
          </>
        )}
      </button>

      {/* Gate Campaign Tooltip on Hover */}
      {showTooltip && (
        <div
          className="absolute top-full right-0 mt-2 w-80 rounded-xl shadow-2xl z-[100] animate-slide-in overflow-hidden"
          style={{
            boxShadow: `0 0 30px rgba(23, 230, 161, 0.5), 0 0 20px rgba(35, 84, 230, 0.4)`,
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* Gradient border effect */}
          <div
            className="p-[2px] rounded-xl"
            style={{
              background: `linear-gradient(135deg, ${GATE_BLUE}, ${GATE_GREEN}, ${GATE_BLUE})`,
            }}
          >
            <div
              className="p-4 rounded-[10px]"
              style={{
                background: `linear-gradient(135deg, rgba(35, 84, 230, 0.15) 0%, rgba(23, 230, 161, 0.15) 100%)`,
                backgroundColor: '#1a1a2e',
              }}
            >
              {/* Header with Icon */}
              <div className="flex items-center gap-2 mb-3">
                <div className="rounded-lg px-2 py-1" style={{ background: 'rgba(240, 240, 240, 0.95)' }}>
                  <img src="/gate-color-short.png" alt="Gate" className="h-4" />
                </div>
                <h4
                  className="text-base font-bold"
                  style={{
                    background: `linear-gradient(90deg, ${GATE_GREEN}, #FFFFFF, ${GATE_GREEN})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  2x Bonus Points
                </h4>
              </div>

              {/* Content */}
              <p className="text-sm" style={{ color: '#FFFFFF', fontFamily: 'Outfit, sans-serif', lineHeight: '1.6' }}>
                Connect with{' '}
                <strong style={{ color: GATE_GREEN }}>Gate Wallet</strong> to earn{' '}
                <strong style={{ color: GATE_GREEN }}>2x the points</strong> (up to{' '}
                <strong style={{ color: GATE_GREEN }}>20,000</strong>) on all chest opening rewards!
              </p>

              {/* Campaign end info */}
              <div
                className="mt-3 pt-3 flex items-center justify-between"
                style={{ borderTop: `1px solid rgba(23, 230, 161, 0.3)` }}
              >
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'Outfit, sans-serif' }}>
                  Campaign ends
                </span>
                <span
                  className="text-xs font-semibold px-2 py-1 rounded"
                  style={{
                    background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
                    color: '#FFFFFF',
                    fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  Dec 31, 2025
                </span>
              </div>
            </div>
          </div>

          {/* Arrow pointing to button */}
          <div
            className="absolute -top-2 right-8 w-4 h-4 rotate-45"
            style={{
              background: `linear-gradient(135deg, ${GATE_BLUE}, ${GATE_GREEN})`,
            }}
          />
        </div>
      )}
    </div>
  );
}
